import { Injectable, OnDestroy, inject } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Types for better type safety
type StorageKey = string;
type StorageValue = string | number | boolean | object | null;

interface StorageResult<T = any> {
  success: boolean;
  value?: T;
  error?: string;
}

interface StorageStats {
  totalKeys: number;
  keys: string[];
  estimatedSize: number;
}

// Constants
const STORAGE_CONFIG = {
  KEY_PREFIX: 'cmc_',
  MAX_KEY_LENGTH: 100,
  MAX_VALUE_SIZE: 1024 * 1024, // 1MB limit
  ENCODING: 'utf-8'
} as const;

@Injectable({
  providedIn: 'root'
})
export class StorageService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  
  // Storage state observables
  public readonly isReady$ = new BehaviorSubject<boolean>(true);
  public readonly operationCount$ = new BehaviorSubject<number>(0);
  
  // Statistics
  private operationCount = 0;

  constructor() {
    console.log('💾 StorageService initialized');
  }

  /**
   * Store a string value
   */
  public async setString(key: StorageKey, value: string): Promise<StorageResult<void>> {
    try {
      this.validateKey(key);
      
      // Ensure value is a string
      const stringValue = this.ensureString(value);
      this.validateStringValue(stringValue);
      
      const prefixedKey = this.getPrefixedKey(key);
      await Preferences.set({ key: prefixedKey, value: stringValue });
      
      this.incrementOperationCount();
      // console.log(`💾 String stored: ${key}`);
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to store string for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Retrieve a string value
   */
  public async getString(key: StorageKey): Promise<StorageResult<string | null>> {
    try {
      this.validateKey(key);
      
      const prefixedKey = this.getPrefixedKey(key);
      const result = await Preferences.get({ key: prefixedKey });
      
      this.incrementOperationCount();
      
      // Ensure the returned value is a string or null
      let returnValue: string | null = null;
      
      if (result.value !== null && result.value !== undefined) {
        returnValue = this.ensureString(result.value);
      }
      
      return {
        success: true,
        value: returnValue
      };
    } catch (error) {
      const errorMessage = `Failed to retrieve string for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Store an object (serialized as JSON)
   */
  public async setObject<T extends StorageValue>(key: StorageKey, value: T): Promise<StorageResult<void>> {
    try {
      this.validateKey(key);
      
      // Ensure we have a valid value to serialize
      if (value == undefined) {
        throw new Error('Cannot store undefined value');
      }
      
      const serializedValue = JSON.stringify(value);
      // Ensure the serialized result is a string
      const stringValue = this.ensureString(serializedValue);
      this.validateStringValue(stringValue);
      
      const prefixedKey = this.getPrefixedKey(key);
      await Preferences.set({ key: prefixedKey, value: stringValue });
      
      this.incrementOperationCount();
      console.log(`💾 Object stored: ${key}`, typeof value);
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to store object for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Retrieve an object (deserialized from JSON)
   */
  public async getObject<T = any>(key: StorageKey): Promise<StorageResult<T | null>> {
    try {
      this.validateKey(key);
      
      const prefixedKey = this.getPrefixedKey(key);
      const result = await Preferences.get({ key: prefixedKey });
      
      this.incrementOperationCount();
      
      if (result.value == null || result.value == undefined) {
        return {
          success: true,
          value: null
        };
      }

      try {
        const parsedValue = JSON.parse(result.value);
        return {
          success: true,
          value: parsedValue
        };
      } catch (parseError) {
        console.warn(`⚠️ Failed to parse JSON for key '${key}':`, parseError);
        return {
          success: true,
          value: result.value as any // Return as string if parsing fails
        };
      }
    } catch (error) {
      const errorMessage = `Failed to retrieve object for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Store a number value
   */
  public async setNumber(key: StorageKey, value: number): Promise<StorageResult<void>> {
    try {
      if (!Number.isFinite(value)) {
        throw new Error('Value must be a finite number');
      }
      
      // Ensure the number is converted to a string
      const stringValue = this.ensureString(value.toString());
      return await this.setString(key, stringValue);
    } catch (error) {
      const errorMessage = `Failed to store number for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Retrieve a number value
   */
  public async getNumber(key: StorageKey): Promise<StorageResult<number | null>> {
    try {
      const result = await this.getString(key);
      
      if (!result.success || result.value == null) {
        return {
          success: result.success,
          value: null,
          error: result.error
        };
      }

      const numValue = Number(result.value);
      
      if (Number.isNaN(numValue)) {
        return {
          success: false,
          error: `Stored value '${result.value}' is not a valid number`
        };
      }

      return {
        success: true,
        value: numValue
      };
    } catch (error) {
      const errorMessage = `Failed to retrieve number for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Store a boolean value
   */
  public async setBoolean(key: StorageKey, value: boolean): Promise<StorageResult<void>> {
    // Ensure the boolean is converted to a string
    const stringValue = this.ensureString(value.toString());
    return await this.setString(key, stringValue);
  }

  /**
   * Retrieve a boolean value
   */
  public async getBoolean(key: StorageKey): Promise<StorageResult<boolean | null>> {
    try {
      const result = await this.getString(key);
      
      if (!result.success || result.value == null) {
        return {
          success: result.success,
          value: null,
          error: result.error
        };
      }

      const boolValue = result.value.toLowerCase() == 'true';
      
      return {
        success: true,
        value: boolValue
      };
    } catch (error) {
      const errorMessage = `Failed to retrieve boolean for key '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Check if a key exists in storage
   */
  public async hasKey(key: StorageKey): Promise<StorageResult<boolean>> {
    try {
      const result = await this.getString(key);
      return {
        success: true,
        value: result.success && result.value !== null
      };
    } catch (error) {
      const errorMessage = `Failed to check key existence '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get all storage keys (with optional prefix filtering)
   */
  public async getKeys(filterPrefix?: string): Promise<StorageResult<string[]>> {
    try {
      const { keys } = await Preferences.keys();
      
      let filteredKeys = keys
        .filter(key => key.startsWith(STORAGE_CONFIG.KEY_PREFIX))
        .map(key => key.replace(STORAGE_CONFIG.KEY_PREFIX, ''));

      if (filterPrefix) {
        filteredKeys = filteredKeys.filter(key => key.startsWith(filterPrefix));
      }

      this.incrementOperationCount();
      
      return {
        success: true,
        value: filteredKeys
      };
    } catch (error) {
      const errorMessage = `Failed to retrieve keys: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Remove a specific item from storage
   */
  public async removeItem(key: StorageKey): Promise<StorageResult<void>> {
    try {
      this.validateKey(key);
      
      const prefixedKey = this.getPrefixedKey(key);
      await Preferences.remove({ key: prefixedKey });
      
      this.incrementOperationCount();
      console.log(`🗑️ Item removed: ${key}`);
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to remove item '${key}': ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Remove multiple items from storage
   */
  public async removeItems(keys: StorageKey[]): Promise<StorageResult<void>> {
    try {
      const results = await Promise.allSettled(
        keys.map(key => this.removeItem(key))
      );

      const failedKeys = results
        .map((result, index) => ({ result, key: keys[index] }))
        .filter(({ result }) => result.status == 'rejected' || !result.value.success)
        .map(({ key }) => key);

      if (failedKeys.length > 0) {
        throw new Error(`Failed to remove keys: ${failedKeys.join(', ')}`);
      }

      console.log(`🗑️ Multiple items removed: ${keys.length}`);
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to remove multiple items: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Clear all storage data
   */
  public async clear(): Promise<StorageResult<void>> {
    try {
      await Preferences.clear();
      
      this.incrementOperationCount();
      console.log('🧹 Storage cleared');
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to clear storage: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get storage statistics
   */
  public async getStats(): Promise<StorageResult<StorageStats>> {
    try {
      const keysResult = await this.getKeys();
      
      if (!keysResult.success) {
        throw new Error(keysResult.error);
      }

      const keys = keysResult.value || [];
      let estimatedSize = 0;

      // Estimate storage size
      for (const key of keys) {
        const result = await this.getString(key);
        if (result.success && result.value) {
          estimatedSize += result.value.length * 2; // Rough UTF-16 estimation
        }
      }

      const stats: StorageStats = {
        totalKeys: keys.length,
        keys,
        estimatedSize
      };

      return {
        success: true,
        value: stats
      };
    } catch (error) {
      const errorMessage = `Failed to get storage stats: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Backup storage data
   */
  public async backup(): Promise<StorageResult<Record<string, any>>> {
    try {
      const keysResult = await this.getKeys();
      
      if (!keysResult.success) {
        throw new Error(keysResult.error);
      }

      const keys = keysResult.value || [];
      const backup: Record<string, any> = {};

      for (const key of keys) {
        const result = await this.getObject(key);
        if (result.success) {
          backup[key] = result.value;
        }
      }

      console.log(`💾 Storage backup created with ${keys.length} items`);
      
      return {
        success: true,
        value: backup
      };
    } catch (error) {
      const errorMessage = `Failed to create backup: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Restore storage data from backup
   */
  public async restore(backup: Record<string, any>): Promise<StorageResult<void>> {
    try {
      const keys = Object.keys(backup);
      
      for (const key of keys) {
        const result = await this.setObject(key, backup[key]);
        if (!result.success) {
          throw new Error(`Failed to restore key '${key}': ${result.error}`);
        }
      }

      console.log(`💾 Storage restored with ${keys.length} items`);
      
      return { success: true };
    } catch (error) {
      const errorMessage = `Failed to restore backup: ${error}`;
      console.error('❌', errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Private utility methods
  private validateKey(key: StorageKey): void {
    if (!key || typeof key !== 'string') {
      throw new Error('Key must be a non-empty string');
    }
    
    if (key.length > STORAGE_CONFIG.MAX_KEY_LENGTH) {
      throw new Error(`Key length exceeds maximum of ${STORAGE_CONFIG.MAX_KEY_LENGTH} characters`);
    }
  }

  private ensureString(value: any): string {
    if (value == null) {
      return 'null';
    }
    
    if (value == undefined) {
      throw new Error('Cannot convert undefined to string');
    }
    
    if (typeof value == 'string') {
      return value;
    }
    
    if (typeof value == 'number') {
      if (!Number.isFinite(value)) {
        throw new Error('Cannot convert non-finite number to string');
      }
      return value.toString();
    }
    
    if (typeof value == 'boolean') {
      return value.toString();
    }
    
    if (typeof value == 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        throw new Error(`Cannot stringify object: ${error}`);
      }
    }
    
    // Fallback for other types
    try {
      return String(value);
    } catch (error) {
      throw new Error(`Cannot convert value to string: ${error}`);
    }
  }

  private validateStringValue(value: string): void {
    if (typeof value !== 'string') {
      throw new Error(`Value must be a string, received ${typeof value}`);
    }
    
    if (value.length > STORAGE_CONFIG.MAX_VALUE_SIZE) {
      throw new Error(`Value size exceeds maximum of ${STORAGE_CONFIG.MAX_VALUE_SIZE} bytes`);
    }
  }

  private getPrefixedKey(key: StorageKey): string {
    return `${STORAGE_CONFIG.KEY_PREFIX}${key}`;
  }

  private incrementOperationCount(): void {
    this.operationCount++;
    this.operationCount$.next(this.operationCount);
  }

  // Getters for component use
  public getOperationCount(): number {
    return this.operationCount;
  }

  public isReady(): boolean {
    return this.isReady$.getValue();
  }

  ngOnDestroy(): void {
    console.log('🧹 StorageService destroyed');
    
    this.destroy$.next();
    this.destroy$.complete();
    
    // Complete observables
    this.isReady$.complete();
    this.operationCount$.complete();
    
    console.log('✅ StorageService cleanup complete');
  }
}
